/**
 * BrandingCard — owner-only inline settings card for white-labelling
 * the tenant's app shell.
 *
 * Lets the tenant owner edit:
 *   • app_name        (e.g. "Rogue" / "SalesFlow")
 *   • ai_name         (e.g. "Rogi" / "Hidro")
 *   • primary_color   (CSS var --brand-primary)
 *   • accent_color    (CSS var --brand-accent)
 *   • logo_url        (sidebar + login + mobile header)
 *   • footer_text     (login-page sub-copy)
 *
 * The form is INLINE-EXPANDABLE per the Trust Contract (no modals).
 * Owners hit "Save" and the call to PUT /api/tenants/{tid} merges over
 * existing branding without wiping the other fields. The TenantBranding
 * context refreshes immediately so the UI reflects the new look without
 * a page reload.
 */
import { useEffect, useState } from "react";
import { Loader2, Palette, RotateCcw, Save } from "lucide-react";
import api from "../../lib/api";
import { useBranding, useFeature } from "../../context/TenantBrandingContext";
import { useAuth } from "../../context/AuthContext";
import { toast } from "../../components/ui/sonner";

export default function BrandingCard() {
  const { user } = useAuth();
  const { branding, refresh, tenantId, planLabel } = useBranding();
  const hasWhiteLabel = useFeature("white_label");
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    app_name: "",
    ai_name: "",
    primary_color: "#1FB6C1",
    accent_color: "#A78BFA",
    logo_url: "",
    footer_text: "",
  });
  const [saving, setSaving] = useState(false);

  // Only owners can change branding.
  const isOwner = user?.role === "owner";

  useEffect(() => {
    if (!branding) return;
    setForm({
      app_name: branding.app_name || "",
      ai_name: branding.ai_name || "",
      primary_color: branding.primary_color || "#1FB6C1",
      accent_color: branding.accent_color || "#A78BFA",
      logo_url: branding.logo_url || "",
      footer_text: branding.footer_text || "",
    });
  }, [branding]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault?.();
    if (!isOwner || !tenantId) return;
    setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}`, {
        branding: {
          app_name: form.app_name || null,
          ai_name: form.ai_name || null,
          primary_color: form.primary_color,
          accent_color: form.accent_color,
          logo_url: form.logo_url || null,
          footer_text: form.footer_text || null,
        },
      });
      await refresh();
      toast.success("Branding updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save branding.");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    set("app_name", "Rogue");
    set("ai_name", "Rogi");
    set("primary_color", "#1FB6C1");
    set("accent_color", "#A78BFA");
    set("logo_url", "");
    set("footer_text", "");
  };

  if (!isOwner) {
    // Non-owners see a read-only summary so they understand what
    // controls the look of their app.
    return (
      <div className="hv-card p-5" data-testid="branding-card-readonly">
        <h2 className="font-display text-2xl font-bold mb-2">Branding</h2>
        <p className="text-sm text-[#a0aabf]">
          Your app shell — colours, logo, and the assistant name (
          <span className="text-white font-medium">{branding?.ai_name || "Hidro"}</span>) — is
          configured by your account owner. Ask them to update branding in Settings.
        </p>
      </div>
    );
  }

  // [2026-02-19 — Stage 3 prep] White-label is a paid feature. Show
  // an upsell card instead of the editor when the tenant's plan
  // doesn't include `white_label`. Hidroverse (enterprise) and Growth+
  // tenants see the full editor.
  if (!hasWhiteLabel) {
    return (
      <div className="hv-card p-5" data-testid="branding-card-upsell">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-md bg-amber-500/15 border border-amber-400/40 flex items-center justify-center shrink-0">
            <Palette className="w-5 h-5 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-2xl font-bold">Branding</h2>
              <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200">
                🔒 Locked on {planLabel || "your current"} plan
              </span>
            </div>
            <p className="text-sm text-[#a0aabf] mt-1 max-w-prose">
              White-labelling — rename the app, rename your AI assistant, swap the
              logo and accent colours — is available on the <span className="text-white font-medium">Growth</span> plan
              and higher. Your current AI assistant is{" "}
              <span className="text-white font-medium">{branding?.ai_name || "Rogi"}</span>.
            </p>
            <a
              href="https://geminii-ai.com/space/rogue/pricing"
              target="_blank" rel="noreferrer"
              className="hv-btn hv-btn-primary inline-flex items-center gap-1.5 text-sm mt-3"
              data-testid="branding-upgrade-cta"
            >
              See plans & upgrade
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hv-card p-5" data-testid="branding-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[var(--brand-primary,#1FB6C1)]/15 border border-[var(--brand-primary,#1FB6C1)]/40 flex items-center justify-center shrink-0">
            <Palette className="w-5 h-5 text-[var(--brand-primary,#1FB6C1)]" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold">Branding</h2>
            <p className="text-sm text-[#a0aabf] mt-1 max-w-prose">
              White-label your workspace: rename the app, rename your AI assistant,
              swap the logo and colour palette. Changes apply instantly across the UI.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="branding-toggle"
        >
          {expanded ? "Collapse" : "Edit branding"}
        </button>
      </div>

      {expanded && (
        <form onSubmit={save} className="mt-5 space-y-4" data-testid="branding-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="App name" hint="What this app is called (e.g. SalesFlow, Rogue)">
              <input
                className="hv-input"
                value={form.app_name}
                onChange={(e) => set("app_name", e.target.value)}
                maxLength={32}
                placeholder="Rogue"
                data-testid="branding-app-name"
              />
            </Field>
            <Field label="AI assistant name" hint="What your in-app AI is called (e.g. Hidro, Rogi)">
              <input
                className="hv-input"
                value={form.ai_name}
                onChange={(e) => set("ai_name", e.target.value)}
                maxLength={24}
                placeholder="Rogi"
                data-testid="branding-ai-name"
              />
            </Field>
            <Field label="Primary colour" hint="Accent buttons & links">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => set("primary_color", e.target.value)}
                  className="w-12 h-9 rounded border border-white/10 bg-transparent"
                  data-testid="branding-primary-picker"
                />
                <input
                  className="hv-input flex-1"
                  value={form.primary_color}
                  onChange={(e) => set("primary_color", e.target.value)}
                  maxLength={9}
                  data-testid="branding-primary-text"
                />
              </div>
            </Field>
            <Field label="Accent colour" hint="Secondary highlights">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => set("accent_color", e.target.value)}
                  className="w-12 h-9 rounded border border-white/10 bg-transparent"
                  data-testid="branding-accent-picker"
                />
                <input
                  className="hv-input flex-1"
                  value={form.accent_color}
                  onChange={(e) => set("accent_color", e.target.value)}
                  maxLength={9}
                  data-testid="branding-accent-text"
                />
              </div>
            </Field>
            <Field label="Logo URL" hint="Public HTTPS URL. ≥256px tall PNG recommended." className="md:col-span-2">
              <input
                className="hv-input"
                value={form.logo_url}
                onChange={(e) => set("logo_url", e.target.value)}
                placeholder="https://…"
                data-testid="branding-logo-url"
              />
              {form.logo_url && (
                <div className="mt-2 flex items-center gap-3 text-xs text-[#647087]">
                  <img
                    src={form.logo_url}
                    alt="logo preview"
                    className="h-12 w-auto object-contain bg-black/30 rounded p-1.5"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    data-testid="branding-logo-preview"
                  />
                  <span>Live preview</span>
                </div>
              )}
            </Field>
            <Field label="Footer text" hint="Optional small print on the login page." className="md:col-span-2">
              <input
                className="hv-input"
                value={form.footer_text}
                onChange={(e) => set("footer_text", e.target.value)}
                placeholder="Trusted by your team & crew"
                maxLength={120}
                data-testid="branding-footer-text"
              />
            </Field>
          </div>

          <div className="flex items-center gap-2 pt-3">
            <button
              type="submit"
              className="hv-btn hv-btn-primary inline-flex items-center gap-1.5"
              disabled={saving}
              data-testid="branding-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save branding
            </button>
            <button
              type="button"
              onClick={resetToDefaults}
              className="hv-btn hv-btn-ghost inline-flex items-center gap-1.5 text-xs"
              data-testid="branding-reset"
              title="Reset form to Rogue / Rogi defaults — does not save until you click Save."
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset to defaults
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-wider text-[#a0aabf]">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-[#647087] mt-1">{hint}</div>}
    </div>
  );
}
